<template>
  <div class="fechamentos-page">
    <!-- Toolbar -->
    <div class="toolbar">
      <franchise-select
        v-model="selectedFranchiseId"
        :label="t('Franquia')"
        elem-id="fechamentos-franchise-filter"
        data-testid="select-franchise-fechamentos"
        data-tutorial="fechamentos-franchise-filter"
      />
      <div class="toolbar-actions">
        <Button
          v-if="canCreate"
          @click="openCreateModal"
          :disabled="isLoading || hasOpenPeriod"
          severity="primary"
          :label="t('Novo Fechamento')"
          icon="pi pi-plus"
          data-testid="btn-create-fechamento"
          data-tutorial="btn-create-fechamento"
          v-tooltip.top="hasOpenPeriod ? t('Já existe um fechamento aberto. Feche o período atual antes de criar um novo.') : t('Criar novo fechamento mensal')"
        />
      </div>
    </div>

    <!-- Inline Dashboard -->
    <template v-if="tableData.length > 0">
      <!-- Dashboard Loading -->
      <div v-if="isLoadingSelected" class="text-center py-5" data-testid="fechamentos-dashboard-loading">
        <spinner style="width:40px;height:40px;" />
      </div>

      <FechamentosDetalhes
        v-else-if="selectedFechamento"
        :fechamento="selectedFechamento"
        @close="handleCloseFromDashboard"
        @refresh="handleDashboardRefresh"
        class="mb-4"
        data-tutorial="fechamentos-detalhes"
      />
    </template>

    <!-- DataTable -->
    <div class="data-table-wrapper">
      <data-table
        ref="dataTableRef"
        :config="datatableConfig"
        :data="tableData"
        :filterConfig="filterConfig"
        :refreshButton="{ show: true }"
        class="fechamentos-table"
        @edit-click="handleEditClick"
        @button-click="handleButtonClick"
        @refresh="loadFechamentos"
        data-testid="fechamentos-table"
        data-tutorial="fechamentos-table"
      />
    </div>

    <!-- Empty state -->
    <div v-if="!isLoading && tableData.length === 0" class="empty-state">
      <i class="pi pi-calendar" style="font-size: 3rem; color: #d1d5db;"></i>
      <h3 class="empty-state-title">{{ t('Nenhum fechamento encontrado') }}</h3>
      <p class="empty-state-text">
        {{ t('Crie um fechamento mensal para acompanhar receitas, despesas e metas do período.') }}
      </p>
      <p class="empty-state-hint">
        {{ t('Cada fechamento usa o Plano de Contas ativo da franquia para comparar previsto vs realizado.') }}
      </p>
      <Button
        v-if="canCreate"
        :label="t('Criar primeiro fechamento')"
        icon="pi pi-plus"
        severity="primary"
        @click="openCreateModal"
        class="mt-3"
        data-testid="btn-create-first-fechamento"
      />
    </div>

    <!-- FechamentosModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { permissionsService } from '../../services/permissionsService';

import { useFechamentos } from '../../composables/useFechamentos';
import FechamentosDetalhes from './FechamentosDetalhes.vue';
import type { Fechamentos_ShowResponseDados } from '../../contracts/Fechamentos_ShowResponse';

type Id = number | string;

/**
 * List/detail row shape used by the page table and the inline dashboard.
 *
 * Backed by the Fechamentos_ShowResponseDados contract (single record).
 * `franquia_nome` is a UI-only enrichment injected client-side after
 * resolving the franchise via permissionsService — see `loadFechamentos()`.
 */
interface FechamentoListItem extends Fechamentos_ShowResponseDados {
  franquia_nome?: string;
}

interface ButtonClickPayload {
  value: Id;
  data?: FechamentoListItem;
  action: string;
}

interface DataTableInstance {
  [key: string]: unknown;
}

const { t } = useI18n();
const {
  isLoading,
  fetchFechamentos,
  fetchFechamento
} = useFechamentos();

// Reactive data
const tableData = ref<FechamentoListItem[]>([]);
const dataTableRef = ref<DataTableInstance | null>(null);
const selectedFranchiseId = ref<Id | null>(null);

// Period selection for inline dashboard
const selectedFechamentoId = ref<Id | null>(null);
const selectedFechamento = ref<FechamentoListItem | null>(null);
const isLoadingSelected = ref<boolean>(false);

// Permissions
const canCreate = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente', 'Financeiro']);
});

const canClose = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
});

const canReopen = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado']);
});

const canDelete = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado']);
});

const hasOpenPeriod = computed<boolean>(() => {
  return tableData.value.some(f => f.status === 'aberto');
});

// Column configuration
const tableColumns = computed(() => {
  const columns: Record<string, unknown>[] = [
    {
      data: 'id',
      title: t('ID'),
      footerType: 'count',
      renderType: 'clickable_id',
      mergedColumns: [
        {
          data: 'franquia_nome',
          title: t('Franquia')
        }
      ]
    },
    {
      data: 'periodo_formatado',
      title: t('Período')
    },
    {
      data: 'meta_faturamento',
      title: t('Meta'),
      renderType: 'currency',
      footerType: 'avg'
    },
    {
      data: 'resumo.total_previsto',
      title: t('Previsto'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'resumo.total_efetivo',
      title: t('Realizado'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'status',
      title: t('Status'),
      cellRenderer: ({ value }: { value: string }) => {
        const isOpen = value === 'aberto';
        const label = isOpen ? t('Aberto') : t('Fechado');
        const bg = isOpen ? '#10b981' : '#6b7280';
        const tooltip = isOpen
          ? t('Período em andamento — recebimentos e despesas sendo contabilizados')
          : t('Período encerrado — dados finalizados');
        return `<span style="display:inline-block;padding:0.25rem 0.75rem;border-radius:9999px;font-size:0.75rem;font-weight:600;color:#fff;background:${bg};cursor:help;" title="${tooltip}">${label}</span>`;
      }
    }
  ];

  if (canClose.value || canReopen.value) {
    const buttons: Record<string, unknown>[] = [];

    if (canClose.value) {
      buttons.push({
        text: t('Fechar'),
        icon: 'pi pi-lock',
        class: 'p-button-warning p-button-sm',
        action: 'close',
        condition: (row: Record<string, unknown>) => row.status === 'aberto',
        testId: (row: Record<string, unknown>) => `fechamento-close-button-${row.id}`
      });
    }

    if (canReopen.value) {
      buttons.push({
        text: t('Reabrir'),
        icon: 'pi pi-lock-open',
        class: 'p-button-warning p-button-sm',
        action: 'reopen',
        condition: (row: Record<string, unknown>) => row.status === 'fechado',
        testId: (row: Record<string, unknown>) => `fechamento-reopen-button-${row.id}`
      });
    }

    columns.push({
      data: null,
      title: t('Ação'),
      sortable: false,
      footerType: 'none',
      renderType: 'action_buttons',
      buttons
    });
  }

  if (canDelete.value) {
    columns.push({
      data: 'id',
      title: t('Excluir'),
      sortable: false,
      footerType: 'none',
      renderType: 'clickable_button',
      buttonText: t('Excluir'),
      buttonIcon: 'pi pi-trash',
      buttonClass: 'p-button-danger p-button-sm',
      buttonAction: 'delete',
      buttonTestId: (rowData: FechamentoListItem) => `fechamento-delete-${rowData.id}-btn`,
      buttonCondition: (rowData: FechamentoListItem) => rowData.status === 'aberto'
    });
  }

  return columns;
});

const datatableConfig = computed(() => ({
  tableId: 'fechamentosDataTable',
  columns: tableColumns.value,
  rowTestId: (row: Record<string, unknown>) => {
    const id = row?.id;
    return id == null ? '' : `fechamento-row-${id}`;
  },
  pagination: true,
  pageLength: 25,
  filterDisplay: 'row',
  heightMode: 'fill-viewport',
  footerLabel: t('Totais')
}));

const filterConfig = computed(() => ({
  columns: {
    'id': 'text',
    'periodo_formatado': 'text',
    'franquia_nome': 'select',
    'meta_faturamento': 'number',
    'status': 'select'
  }
}));

// Methods
const loadFechamentos = async (): Promise<void> => {
  log('FechamentosPage', 'loadFechamentos', 'Loading fechamentos');
  try {
    eventBus.emit('loading.show', t('Carregando fechamentos...'));

    const filters: { franquia_id?: Id } = {};
    if (selectedFranchiseId.value) {
      filters.franquia_id = selectedFranchiseId.value;
    }

    const result = await fetchFechamentos(filters);
    const items: FechamentoListItem[] = (result?.items ?? []) as FechamentoListItem[];
    items.forEach(item => {
      if (!item.franquia_nome && item.franquia_id) {
        const franquia = permissionsService.getFranquiaById(Number(item.franquia_id));
        const dados = (franquia as unknown as { Dados?: { Nome?: string } } | null)?.Dados;
        item.franquia_nome = dados?.Nome || '';
      }
    });
    tableData.value = items;

    autoSelectPeriod();

    log('FechamentosPage', 'loadFechamentos', 'Loaded records', items.length);
  } catch (err) {
    log('FechamentosPage', 'loadFechamentos', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar fechamentos: ') + message);
  } finally {
    eventBus.emit('loading.hide');
  }
};

/**
 * Auto-select the most relevant period: prefer aberto, fallback to most recent (first in list)
 */
const autoSelectPeriod = (): void => {
  if (!tableData.value.length) {
    selectedFechamentoId.value = null;
    selectedFechamento.value = null;
    return;
  }

  // If currently selected period still exists in list, reload it
  if (selectedFechamentoId.value) {
    const stillExists = tableData.value.find(f => f.id === selectedFechamentoId.value);
    if (stillExists) {
      selectPeriod(selectedFechamentoId.value);
      return;
    }
  }

  // Prefer open period
  const openPeriod = tableData.value.find(f => f.status === 'aberto');
  if (openPeriod && openPeriod.id !== undefined) {
    selectPeriod(openPeriod.id);
    return;
  }

  // Fallback: most recent (first item, assuming API returns sorted by date desc)
  const firstId = tableData.value[0].id;
  if (firstId !== undefined) {
    selectPeriod(firstId);
  }
};

/**
 * Select a period and load its full data for the inline dashboard.
 * Shows cached data immediately so the dashboard renders without delay.
 * Then fetches full data in the background to enrich fields like data_inicial / franquia_id.
 */
const selectPeriod = async (id: Id): Promise<void> => {
  if (!id) return;

  log('FechamentosPage', 'selectPeriod', 'Selecting period', id);
  selectedFechamentoId.value = id;

  // Show cached item immediately so the dashboard renders right away
  const cached = tableData.value.find(f => f.id === id);
  if (cached) {
    selectedFechamento.value = cached;
  }

  // If cached item is incomplete, load full data in the background
  if (!cached || !cached.data_inicial || !cached.franquia_id) {
    isLoadingSelected.value = !cached; // only show spinner if nothing to show yet
    try {
      const full = await fetchFechamento(id) as FechamentoListItem;
      selectedFechamento.value = full;
      log('FechamentosPage', 'selectPeriod', 'Period loaded', full);
    } catch (err) {
      log('FechamentosPage', 'selectPeriod', 'Error loading full period data', err);
      // Keep cached partial data if available; only error if nothing is shown
      if (!cached) {
        eventBus.emit('globalMessage.error', t('Erro ao carregar período'));
      }
    } finally {
      isLoadingSelected.value = false;
    }
  }
};

const openCreateModal = (): void => {
  log('FechamentosPage', 'openCreateModal', 'Opening new fechamento modal');
  eventBus.emit('modal.fechamentos.open', { mode: 'create' });
};

/**
 * Clicking a row ID selects that period in the inline dashboard (no modal)
 */
const handleEditClick = (id: Id): void => {
  log('FechamentosPage', 'handleEditClick', 'Selecting period from row click', id);
  selectPeriod(id);
  const dashboardEl = document.querySelector('[data-testid="fechamentos-inline-dashboard"]');
  if (dashboardEl) {
    dashboardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const handleButtonClick = (payload?: ButtonClickPayload): void => {
  if (!payload) return;
  const { value, data, action } = payload;
  const id = (value ?? data?.id) as Id;
  log('FechamentosPage', 'handleButtonClick', 'Button clicked', { id, action });

  switch (action) {
    case 'close':
      handleClose(id, data);
      break;
    case 'reopen':
      handleReopen(id);
      break;
    case 'delete':
      handleDelete(id);
      break;
    default:
      log('FechamentosPage', 'handleButtonClick', 'Unknown action', action);
  }
};

const handleClose = (id: Id, data?: FechamentoListItem): void => {
  log('FechamentosPage', 'handleClose', 'Opening close confirmation modal for', id);
  eventBus.emit('modal.fechamentos.open', { id, mode: 'close', fechamento: data });
};

const handleReopen = (id: Id): void => {
  log('FechamentosPage', 'handleReopen', 'Opening reopen modal for', id);
  eventBus.emit('modal.fechamentos.open', { id, mode: 'reopen' });
};

const handleDelete = (id: Id): void => {
  log('FechamentosPage', 'handleDelete', 'Opening delete modal for', id);
  eventBus.emit('modal.fechamentos.open', { id, mode: 'delete' });
};

/**
 * "Fechar período" button emitted from inline FechamentosDetalhes
 */
const handleCloseFromDashboard = (): void => {
  if (selectedFechamento.value) {
    eventBus.emit('modal.fechamentos.open', {
      id: selectedFechamento.value.id,
      mode: 'close',
      fechamento: selectedFechamento.value
    });
  }
};

/**
 * Refresh the inline dashboard after an action (e.g. recalculate)
 */
const handleDashboardRefresh = async (): Promise<void> => {
  if (!selectedFechamentoId.value) return;
  isLoadingSelected.value = true;
  try {
    const full = await fetchFechamento(selectedFechamentoId.value) as FechamentoListItem;
    selectedFechamento.value = full;
  } catch (err) {
    log('FechamentosPage', 'handleDashboardRefresh', 'Error refreshing dashboard', err);
  } finally {
    isLoadingSelected.value = false;
  }
};

// Event handlers
const handleFechamentoSaved = (): void => {
  log('FechamentosPage', 'event:fechamento.saved', 'Received');
  loadFechamentos();
};

watch(selectedFranchiseId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    log('FechamentosPage', 'watch:selectedFranchiseId', 'Franchise changed', { newId, oldId });
    selectedFechamentoId.value = null;
    selectedFechamento.value = null;
    loadFechamentos();
  }
});

// Lifecycle
onMounted(async () => {
  log('FechamentosPage', 'mounted', 'Component mounted, loading data');

  eventBus.on('fechamento.saved', handleFechamentoSaved);
});

onBeforeUnmount(() => {
  log('FechamentosPage', 'beforeUnmount', 'Cleaning up listeners');

  eventBus.off('fechamento.saved', handleFechamentoSaved);
});

// Expose for parent access
defineExpose({
  loadFechamentos,
  openCreateModal
});
</script>

<style scoped>
.fechamentos-page {
  width: 100%;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
}

.data-table-wrapper {
  margin-top: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state-title {
  margin-top: 16px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.empty-state-text {
  margin-top: 8px;
  color: #6b7280;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.empty-state-hint {
  margin-top: 8px;
  color: #9ca3af;
  font-size: 0.875rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Inline Dashboard */
.inline-dashboard {
  border-radius: 8px;
}

/* DataTable refresh button — yellow border + text, white bg */
.fechamentos-table :deep(.datatable-toolbar .p-button) {
  border-color: #f59e0b !important;
  color: #f59e0b !important;
  background: white !important;
}

.fechamentos-table :deep(.datatable-toolbar .p-button:hover) {
  background: #fffbeb !important;
}
</style>
